import { useEffect, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'remeda'
import { ErrorHandler } from '@components/CartDrawer/ErrorHandler/ErrorHandler'
import { transformProductToWishlistLineItem } from '@components/WishlistLineItem/transformProductToWishlistLineItem'
import { WishlistLineItem } from '@components/WishlistLineItem/WishlistLineItem'
import { useWishlistProducts } from '@components/WishlistProvider/useWishlistProducts'
import { useWishlist } from '@components/WishlistProvider/WishlistProvider'
import { Button } from '@velasca/ui/button'
import { Drawer } from '@velasca/ui/drawer'
import { ErrorFallback } from '@velasca/ui/error-fallback'
import * as S from './WishlistDrawer.styles'

// Check if user is a guest (not logged in)
const isGuestUser = (): boolean => {
  return typeof window !== 'undefined' && window.customerID === 'unknown'
}

export const WishlistDrawer = () => {
  const { t } = useTranslation('global')
  const {
    isDrawerOpen,
    setIsDrawerOpen,
    wishlistItems: providerWishlistItems,
    removeProduct,
    refreshWishlist,
  } = useWishlist()

  // Local state for optimistic updates
  const [optimisticallyRemovedIds, setOptimisticallyRemovedIds] = useState<
    Set<number>
  >(new Set())

  // Track if we need to refresh when drawer reopens
  const needsRefreshRef = useRef(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Reset optimistic removals and sync with server when drawer opens
  useEffect(() => {
    if (isDrawerOpen) {
      // If we flagged that we need to refresh, do it now
      if (needsRefreshRef.current) {
        setIsRefreshing(true)
        refreshWishlist()
          .then(() => {
            needsRefreshRef.current = false
            setIsRefreshing(false)
          })
          .catch((error) => {
            console.error('Failed to refresh wishlist:', error)
            needsRefreshRef.current = false
            setIsRefreshing(false)
          })
      }
      setOptimisticallyRemovedIds(new Set())
    }
  }, [isDrawerOpen, refreshWishlist])

  // Fetch rich product data from Shopify
  const {
    loading: productsLoading,
    wishlistProducts: allWishlistProducts,
    error: productsError,
  } = useWishlistProducts(providerWishlistItems, !isDrawerOpen || isRefreshing)

  // Filter products after GraphQL query to avoid re-running the hook
  const wishlistProducts = allWishlistProducts.filter((product) => {
    const productIdFromGid = product.id.replace('gid://shopify/Product/', '')
    const productId = Number(productIdFromGid)

    return !optimisticallyRemovedIds.has(productId)
  })

  return (
    <>
      <S.WishlistDrawerGlobalStyles />
      <Drawer
        className="wishlist-drawer"
        isOpen={isDrawerOpen}
        aria-label={t('wishlistModalTitle')}
        close={() => {
          setIsDrawerOpen(false)
        }}
      >
        <Drawer.Header
          title={t('wishlistModalTitle')}
          onClose={() => {
            setIsDrawerOpen(false)
          }}
        />
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <ErrorHandler>
            <Drawer.Content
              isLoading={productsLoading || isRefreshing}
              className="wishlist-drawer-content"
              aria-live="polite"
              aria-busy={productsLoading || isRefreshing}
            >
              <S.ContentContainer>
                {isEmpty(providerWishlistItems) ? (
                  <S.EmptyMessage>{t('wishlistEmpty')}</S.EmptyMessage>
                ) : isEmpty(wishlistProducts) &&
                  !productsLoading &&
                  !isRefreshing ? (
                  <S.EmptyMessage>{t('wishlistEmpty')}</S.EmptyMessage>
                ) : (
                  <>
                    <S.WishlistGrid
                      role="list"
                      aria-label={`Wishlist items, ${wishlistProducts.length} item${wishlistProducts.length !== 1 ? 's' : ''}`}
                    >
                      {wishlistProducts.map((product) => {
                        const lineItemProps =
                          transformProductToWishlistLineItem(product)

                        // Find the original wishlist item that matches this product
                        const originalWishlistItem = providerWishlistItems.find(
                          (item) => {
                            const productIdFromGid = product.id.replace(
                              'gid://shopify/Product/',
                              '',
                            )

                            return item.empi === Number(productIdFromGid)
                          },
                        )

                        // Derive UI hints from Swym cprops
                        const variantSelectedByUser =
                          originalWishlistItem?.cprops
                            ?.variantSelectedByUser === true

                        // Try to compute the human size label from the selected variant id
                        let preselectedSizeLabel: string | undefined

                        if (variantSelectedByUser) {
                          const epiStr = String(originalWishlistItem?.epi ?? '')

                          preselectedSizeLabel =
                            lineItemProps.sizes?.find(
                              (s) => String(s.id) === epiStr,
                            )?.name || preselectedSizeLabel
                        }

                        return (
                          <WishlistLineItem
                            key={product.id || product.handle}
                            onRemove={() => {
                              if (!originalWishlistItem) {
                                console.error(
                                  'Could not find original wishlist item for removal',
                                )

                                return
                              }

                              // Optimistic update: hide product immediately
                              setOptimisticallyRemovedIds(
                                (prev) =>
                                  new Set([...prev, originalWishlistItem.empi]),
                              )

                              // Flag that we need to refresh when drawer reopens
                              needsRefreshRef.current = true

                              // Remove from API
                              removeProduct({
                                handle: product.handle,
                                variantId: `gid://shopify/ProductVariant/${originalWishlistItem.epi}`,
                                productId: product.id,
                              }).catch((error) => {
                                console.error(
                                  'Failed to remove product from wishlist:',
                                  error,
                                )
                                // Rollback on error
                                setOptimisticallyRemovedIds((prev) => {
                                  const newSet = new Set(prev)

                                  newSet.delete(originalWishlistItem.empi)

                                  return newSet
                                })
                                // Reset refresh flag since removal failed
                                needsRefreshRef.current = false
                              })
                            }}
                            {...lineItemProps}
                            preselectedSizeLabel={preselectedSizeLabel}
                          />
                        )
                      })}
                      {productsError && (
                        <S.ErrorMessage>
                          Error loading products: {productsError.message}
                        </S.ErrorMessage>
                      )}
                    </S.WishlistGrid>
                    {isGuestUser() && (
                      <S.Actions
                        role="region"
                        aria-label="Account actions"
                        aria-describedby="guest-user-message"
                      >
                        <div id="guest-user-message" className="sr-only">
                          Create an account to save your wishlist items across
                          devices
                        </div>
                        <Button
                          fullWidth
                          as="a"
                          href="/account/login"
                          variant="primary"
                          text={t('wishlistLoginButton')}
                          aria-describedby="login-description"
                        />
                        <div id="login-description" className="sr-only">
                          Sign in to access your saved wishlist
                        </div>
                        <Button
                          fullWidth
                          as="a"
                          href="/account/register"
                          variant="ghost"
                          text={t('wishlistRegisterButton')}
                          aria-describedby="register-description"
                        />
                        <div id="register-description" className="sr-only">
                          Create a new account to start saving items
                        </div>
                      </S.Actions>
                    )}
                  </>
                )}
              </S.ContentContainer>
            </Drawer.Content>
          </ErrorHandler>
        </ErrorBoundary>
      </Drawer>
    </>
  )
}
